import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { SeverityLevel } from '@microsoft/applicationinsights-web';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { select, Store } from '@ngrx/store';

import { of } from 'rxjs';
import { catchError, concatMap, exhaustMap, map, switchMap, withLatestFrom } from 'rxjs/operators';

import { DeviceGroupSelectors } from 'src/app/features/device-group/store/selectors';
import { DocumentsActions } from 'src/app/features/documents';
import { ModalsActions } from 'src/app/features/modals';
import { NotificationsActions, NotificationType } from 'src/app/features/notifications';
import {
  AvDisruptionActionBlockingResponse,
  ExceptionType,
} from 'src/app/features/notifications/models';
import { PackageUsersSelectors } from 'src/app/features/package-users';
import { PackagesSelectors } from 'src/app/features/packages';
import { ProductType } from 'src/app/features/packages/models';
import { SignalRActions } from 'src/app/features/signal-r';
import { SigningRoomActions } from 'src/app/features/signing-room/store/actions';
import { ApplicationInsightsService } from 'src/app/services/application-insights.service';
import { RootStoreState } from 'src/app/store';
import { v4 as uuid } from 'uuid';

import { EndorsementType, UniqueEndorsementImage } from '../../models';
import { EndorsementsService } from '../../services';
import { EndorsementsActions } from '../actions';
import { scaffoldUniqueEndorsementImageModel } from '../helpers';
import { EndorsementsSelectors } from '../selectors';
import {
  ActionBlockingSnackbarComponent,
  AvDisruptionActionBlockingSnackbarComponent,
} from 'src/app/features/notifications/components';

@Injectable()
export class EndorsementsEffects {
  // Notification Id constant to prevent multiple toast messages
  notificationId: string;

  configureListeners$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SignalRActions.SignalRRoomJoined),
        map(() => {
          this.endorsementsService.configureListeners();
        })
      ),
    { dispatch: false }
  );

  setEndorsements$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DocumentsActions.SetDocuments),
      switchMap(({ payload, isRecovery }) => {
        if (!payload?.documentEndorsementLocations) {
          return [];
        }

        const { documentEndorsementLocations } = payload;

        if (isRecovery) {
          return [
            EndorsementsActions.MergeEndorsementLocations({
              payload: documentEndorsementLocations,
            }),
            EndorsementsActions.FetchFreeTextEndorsementImages(),
          ];
        }

        const uniqueEndorsementImages = Array<UniqueEndorsementImage>();

        documentEndorsementLocations.forEach((endorsementLocation, index, endorsementLocations) => {
          if (endorsementLocation.endorsementTypeCode === 'TEXTBOX') {
            if (endorsementLocation.endorsementValue) {
              endorsementLocations[index] = {
                ...endorsementLocation,
              };
            }
            uniqueEndorsementImages.push(scaffoldUniqueEndorsementImageModel(endorsementLocation));
            return;
          }

          if (endorsementLocation.endorsementValue && !endorsementLocation.isRequired) {
            endorsementLocations[index] = {
              ...endorsementLocation,
            };
          }
        });

        return [
          EndorsementsActions.SetEndorsementLocations({
            payload: documentEndorsementLocations,
          }),
          EndorsementsActions.CreateUniqueEndorsementImagePlaceholders({
            payload: uniqueEndorsementImages,
          }),
        ];
      })
    )
  );

  updatePackageUserEndorsementsAsSignableOnDevice$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EndorsementsActions.UpdateSignableOnDeviceStatusByPackageUserGuid),
      concatLatestFrom((action) => [
        this.store.pipe(
          select(EndorsementsSelectors.selectEndorsementLocationsByPackageUserGuid, {
            packageUserGuid: action.payload.packageUserGuid,
          })
        ),
      ]),
      switchMap(([action, packageUserEndorsementLocations]) => {
        return [
          EndorsementsActions.SetUpdatedSignableOnDeviceStatus({
            payload: {
              endorsementLocations: packageUserEndorsementLocations,
              enable: action.payload.enable,
            },
          }),
        ];
      })
    )
  );

  refreshPackageUserEndorsementsSignableStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EndorsementsActions.RefreshEndorsementsSignableStatus),
      withLatestFrom(
        this.store.pipe(select(EndorsementsSelectors.selectAllUserEndorsementLocations))
      ),
      switchMap(([action, endorsementLocations]) => [
        EndorsementsActions.RefreshEndorsementsUpdateSignableOnDeviceStatusByPackageUserGuids({
          payload: {
            devicePackageUserGuids: action.payload.devicePackageUserGuids,
            endorsementLocations,
          },
        }),
      ])
    )
  );

  fetchEndorsementImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EndorsementsActions.FetchEndorsementImages),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(this.store.pipe(select(PackageUsersSelectors.getActivePackageUserGuid)))
        )
      ),
      switchMap(([_, packageUserGuid]) =>
        this.endorsementsService.getEndorsementImages(packageUserGuid).pipe(
          map((payload) =>
            EndorsementsActions.SetEndorsementImages({
              payload: payload.packageEndorsements,
            })
          ),
          catchError((err) =>
            of(
              NotificationsActions.AddNotification({
                payload: {
                  exception: err,
                  notificationType: NotificationType.Error,
                  id: uuid(),
                  text: 'Failed to get Endorsement Images',
                  exceptionType: ExceptionType.ReloadRetry,
                },
              })
            )
          )
        )
      )
    )
  );

  fetchFreeTextEndorsementImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EndorsementsActions.FetchFreeTextEndorsementImages),
      switchMap(() =>
        this.endorsementsService.getUniqueEndorsementImages().pipe(
          map((payload) =>
            EndorsementsActions.SetUniqueEndorsementImages({
              payload: payload.packageEndorsementFreeTextImages,
            })
          ),
          catchError((err) =>
            of(
              NotificationsActions.AddNotification({
                payload: {
                  exception: err,
                  notificationType: NotificationType.Error,
                  id: uuid(),
                  text: 'Failed to get Free Text Endorsement Images',
                  exceptionType: ExceptionType.ReloadRetry,
                },
              })
            )
          )
        )
      )
    )
  );

  fetchSystemEndorsementImages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EndorsementsActions.FetchSystemEndorsementImages),
      switchMap(() =>
        this.endorsementsService.getSystemEndorsementImages().pipe(
          map((payload) =>
            EndorsementsActions.SetSystemEndorsementImages({
              payload: payload.packageSystemEndorsements,
            })
          ),
          catchError((err) =>
            of(
              NotificationsActions.AddNotification({
                payload: {
                  exception: err,
                  notificationType: NotificationType.Error,
                  id: uuid(),
                  text: 'Failed to get System Endorsement Images',
                  exceptionType: ExceptionType.ReloadRetry,
                },
              })
            )
          )
        )
      )
    )
  );

  setProcessingEndorsement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        EndorsementsActions.ProcessSignEndorsement,
        EndorsementsActions.ProcessEraseSignedEndorsement
      ),
      map(() =>
        EndorsementsActions.SetProcessingEndorsement({
          payload: { isProcessing: true },
        })
      )
    )
  );

  // this only gets called by the device that is currently "clicking to apply"
  signEndorsement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EndorsementsActions.ProcessSignEndorsement),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(select(PackageUsersSelectors.getActivePackageUserGuid)),
            this.store.pipe(select(PackagesSelectors.getActivePackageGuid)),
            this.store.pipe(select(PackagesSelectors.getProductType))
          )
        )
      ),
      exhaustMap(([action, userGuid, packageGuid, productType]) =>
        this.endorsementsService.signEndorsement(action.payload).pipe(
          switchMap((response: any) => {
            const commonSignEndorsementActions = [
              EndorsementsActions.SignEndorsementCallCompleted(action),
              EndorsementsActions.SetProcessingEndorsement({
                payload: { isProcessing: false },
              }),
              EndorsementsActions.SetEndorsementSigned({
                payload: {
                  endorsementLocationId: action.payload.endorsementLocationId,
                },
              }),
              ...this.getHideLoadingSpinner(productType, true),
            ];

            return [...commonSignEndorsementActions];
          }),
          catchError((error) => {
            const err = error;
            err.message =
              'Sign Endorsement Request Failed' + ' user:' + userGuid + ' package:' + packageGuid;
            this.applicationInsightsService.logException(err, SeverityLevel.Error);

            // Prevent multiple of the same toast from being displayed
            if (this.notificationId) {
              this.store.dispatch(
                NotificationsActions.ClearNotification({
                  payload: { notificationId: this.notificationId },
                })
              );
            }

            this.notificationId = uuid();

            const actions = [
              ModalsActions.HideLoadingSpinner(),
              ModalsActions.ClearModalComponent(),
              EndorsementsActions.RollbackEndorsement({
                payload: {
                  endorsementLocationId: action.payload.endorsementLocationId,
                },
              }),
              EndorsementsActions.SetProcessingEndorsement({
                payload: { isProcessing: false },
              }),
            ];

            if (error.status === HttpStatusCode.Locked) {
              return [
                ...actions,
                NotificationsActions.AddSnackBarNotification({
                  payload: {
                    component:
                      error.error instanceof AvDisruptionActionBlockingResponse
                        ? AvDisruptionActionBlockingSnackbarComponent
                        : ActionBlockingSnackbarComponent,
                    title: error.error.title,
                    body: error.error.message,
                    data: error.error,
                    panelClass: 'mdc-snackbar-error',
                    duration: 12000,
                  },
                }),
              ];
            } else {
              return [
                ...actions,
                NotificationsActions.AddNotification({
                  payload: {
                    notificationType: NotificationType.Info,
                    id: this.notificationId,
                    text: 'Endorsement failed to sign. Please try again.',
                    exceptionType: ExceptionType.DisplayMessage,
                  },
                }),
              ];
            }
          })
        )
      )
    )
  );

  eraseSignedEndorsement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EndorsementsActions.ProcessEraseSignedEndorsement),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(select(PackageUsersSelectors.getActivePackageUserGuid)),
            this.store.pipe(select(PackagesSelectors.getActivePackageGuid)),
            this.store.pipe(select(PackagesSelectors.getProductType))
          )
        )
      ),
      exhaustMap(([action, userGuid, packageGuid, productType]) =>
        this.endorsementsService.eraseEndorsement(action.payload).pipe(
          switchMap(() => {
            const commonEraseEndorsementActions = [
              // not techinially calling to signEndorsement is this needed
              EndorsementsActions.SignEndorsementCallCompleted(action),
              EndorsementsActions.SetProcessingEndorsement({
                payload: { isProcessing: false },
              }),
              EndorsementsActions.RollbackEndorsement({
                payload: {
                  endorsementLocationId: action.payload.endorsementLocationId,
                },
              }),
              ...this.getHideLoadingSpinner(productType, true),
            ];

            if (action.payload.endorsementType === EndorsementType.TEXTBOX) {
              return [
                ...commonEraseEndorsementActions,
                EndorsementsActions.ClearUniqueEndorsementImage({
                  payload: {
                    documentEndorsementLocationId: action.payload.endorsementLocationId,
                  },
                }),
              ];
            }

            return [...commonEraseEndorsementActions];
          }),
          catchError((error) => {
            const err = error;
            err.message =
              'Endorsement Attempt Request Failed' +
              ' user:' +
              userGuid +
              ' package:' +
              packageGuid;
            this.applicationInsightsService.logException(err, SeverityLevel.Error);

            // Prevent multiple of the same toast from being displayed
            if (this.notificationId) {
              this.store.dispatch(
                NotificationsActions.ClearNotification({
                  payload: { notificationId: this.notificationId },
                })
              );
            }

            this.notificationId = uuid();
            const actions = [
              ModalsActions.HideLoadingSpinner(),
              ModalsActions.ClearModalComponent(),
              EndorsementsActions.SetProcessingEndorsement({
                payload: { isProcessing: false },
              }),
            ];

            if (error.status === HttpStatusCode.Locked) {
              return [
                ...actions,
                NotificationsActions.AddSnackBarNotification({
                  payload: {
                    component:
                      error.error instanceof AvDisruptionActionBlockingResponse
                        ? AvDisruptionActionBlockingSnackbarComponent
                        : ActionBlockingSnackbarComponent,
                    title: error.error.title,
                    body: error.error.message,
                    data: error.error,
                    panelClass: 'mdc-snackbar-error',
                    duration: 12000,
                  },
                }),
              ];
            } else {
              return [
                ...actions,
                NotificationsActions.AddNotification({
                  payload: {
                    notificationType: NotificationType.Info,
                    id: this.notificationId,
                    text: 'Endorsement failed to erase. Please try again.',
                    exceptionType: ExceptionType.DisplayMessage,
                  },
                }),
              ];
            }
          })
        )
      )
    )
  );

  endorsementImageUpdated$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EndorsementsActions.EndorsementImageUpdated),
      switchMap((action) =>
        this.endorsementsService
          .getUpdatedEndorsementImage(action.payload.endorsementId, action.payload.packageUserGuid)
          .pipe(
            switchMap((p: any) => [
              EndorsementsActions.UpdateUniqueEndorsementImage({
                payload: {
                  documentEndorsementLocationId: p.endorsementId,
                  endorsementImageString: p.endorsementImage,
                },
              }),
            ]),
            catchError((err) =>
              of(
                NotificationsActions.AddNotification({
                  payload: {
                    exception: err,
                    notificationType: NotificationType.Error,
                    id: uuid(),
                    text: 'Updated Endorsement Image Request Failed',
                    exceptionType: ExceptionType.ReloadRetry,
                  },
                })
              )
            )
          )
      )
    )
  );

  //TODO: find a better name or remove the action from  EndorrsementAttemptAccepted
  broadcastRollbackendorsement$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(EndorsementsActions.BroadcastRollbackEndorsement),
      concatMap((action) =>
        of(action).pipe(
          withLatestFrom(
            this.store.pipe(
              select(EndorsementsSelectors.isSignableOnDevice(action.payload.endorsementLocationId))
            )
          )
        )
      ),
      switchMap(([action, isSignableOnDevice]) => {
        if (isSignableOnDevice) {
          return [];
        }
        return [EndorsementsActions.RollbackEndorsement(action)];
      })
    );
  });

  getHideLoadingSpinner(productType: ProductType, shouldApplySignatureModalShowing: boolean) {
    if (productType === ProductType.RemoteSigning) {
      if (shouldApplySignatureModalShowing) {
        return [ModalsActions.ClearModalComponent()];
      } else {
        return [];
      }
    } else {
      return [ModalsActions.HideLoadingSpinner()];
    }
  }

  fetchSystemFieldsStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EndorsementsActions.FetchSystemFieldsStatus),
      switchMap(() =>
        this.endorsementsService.getSystemFieldStatus().pipe(
          switchMap((systemFieldsStatus) => [
            EndorsementsActions.SetSystemFieldStatus({ payload: systemFieldsStatus }),
          ]),
          catchError((err) =>
            of(
              NotificationsActions.AddNotification({
                payload: {
                  exception: err,
                  notificationType: NotificationType.Error,
                  id: uuid(),
                  text: 'Failed To Fetch Are All System Endorsements Applied',
                  exceptionType: ExceptionType.CannotProceed,
                },
              })
            )
          )
        )
      )
    )
  );

  updateSignableOnDeviceStatusForDeviceUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(EndorsementsActions.UpdateSignableOnDeviceStatusForDeviceUsers),
      withLatestFrom(this.store.pipe(select(DeviceGroupSelectors.getUsersOnDevice))),
      switchMap(([_, usersOnDevice]) => {
        if (usersOnDevice.length > 1) {
          const actions = [];
          usersOnDevice.forEach((packagUser) => {
            actions.push(
              EndorsementsActions.UpdateSignableOnDeviceStatusByPackageUserGuid({
                payload: { packageUserGuid: packagUser.packageUserGuid, enable: true },
              })
            );
          });
          return actions;
        }

        return [];
      })
    )
  );

  initiateCompleteSigningRoomSession$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SigningRoomActions.InitiateCompleteSigningRoomSession),
      concatLatestFrom(() => of(undefined)),
      exhaustMap(([_]) =>
        this.endorsementsService.auditEndorsements().pipe(
          switchMap(() => {
            return [SigningRoomActions.CompleteSigningRoomSession()];
          }),
          catchError((err) =>
            of(
              NotificationsActions.AddNotification({
                payload: {
                  exception: err,
                  notificationType: NotificationType.Error,
                  id: uuid(),
                  text: 'Failed to recitify missing endorsements during endorsement audit',
                  exceptionType: ExceptionType.CannotProceed,
                },
              })
            )
          )
        )
      )
    )
  );

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store<RootStoreState.State>,
    private readonly endorsementsService: EndorsementsService,
    private readonly applicationInsightsService: ApplicationInsightsService
  ) {}
}
